"use client";

import { signIn, useSession } from "next-auth/react";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useEffect, useState, useRef } from "react";
import { IoShareSocialOutline } from "react-icons/io5";
import { LuChevronDown, LuChevronUp, LuExternalLink } from "react-icons/lu";
import { RiArrowRightUpLine, RiPencilLine } from "react-icons/ri";
import { toast } from "react-toastify";
import HostAnEventButton from "~/app/_components/HostAnEventButton";
import PartnerWithUsButton from "~/app/_components/PartnerWithUsButton";
import { FollowCommunityButtonBySlug } from "~/components/v3/community-components/CommunityHeader/FollowCommunityButton";
import { ConditionalLink, If } from "~/components/v3/Conditional";
import Checkmark from "~/components/v3/icons/Checkmark";
import { SocialLink } from "~/components/v3/icons/Social";
import { Card, CardContent } from "~/components/v3/shadcn/Card";
import { Button } from "~/components/v3/themed/Button";
import ThemedLink from "~/components/v3/themed/ThemedLink";
import { CommunityAvatar } from "~/components/v4/shadcn/CommunityAvatar";
import { env } from "~/env.mjs";
import { UserPermissions } from "~/server/api/helpers/permissionHelpers";
import { useFollowingStore } from "~/state/store";
import { api } from "~/utils/api";
import { GetCommunityDetailsAndExtraDetails } from "~/utils/community-queries";
import { cn } from "~/utils/fontsConfig";
import EditCommunitySheet from "./EditCommunitySheet";
import ShareCommunityModal from "./ShareCommunityModal";
import SubscribeToPublishHeader from "~/components/v3/community-components/CommunityHeader/SubscribeToPublishHeader";
import EventProposeModal from "~/components/v3/modals/ProposeEventModal";
import { Session } from "next-auth";

const DeIdConnect = ({ communitySlug }: { communitySlug: string }) => {
    const { data: session } = useSession();

    const isConnected = session?.user.connectedProviders.find(
        (p) => p === "deid",
    );

    return (
        <If condition={!!session && communitySlug === "degods"}>
            <Button
                variant="secondary"
                className="rounded"
                onClick={() => signIn("deid")}
                disabled={!!isConnected}
            >
                <Image
                    src="/customers/degods_deid.svg"
                    objectFit="cover"
                    className="mr-1"
                    width={24}
                    height={24}
                    alt="de[id] icon"
                />
                {!isConnected ? "connect" : "connected"}
            </Button>
        </If>
    );
};

// TODO: fix when we don't need it anymore
// deets: https://riverplatform.slack.com/archives/C05ABV6NXG9/p1738873038522719
const TEMP_HIGH_BANNER_COMMUNITIES = [
    "joel",
    "marques",
    "david-goggins",
    "steven",
    "lex",
    "monty",
    "sophia",
    "a16z",
    "body-coach",
    "jim",
    "matthew",
    "tom",
    "gretchen",
    "neildegrasse",
    "robinsharma",
    "esther",
    "sadie",
    "daymond",
    "chalene",
    "anniegrace",
    "blogilates",
    "ali",
    "candace",
    "jenna",
    "shawn",
    "laila",
    "kate",
    "jen",
    "gabby",
    "rangan",
    "drjohn",
    "brian",
    "davidbach",
    "tori",
    "johnny-harris",
    "zachchoi",
    "chrissymetz",
    "mrsdowjones",
];

export const CommunityDetailsHeader = ({
    allCommunityDetails,
    permissions,
    hasManagingPermissions,
    hasChapterLeadPermissions,
    session,
}: {
    allCommunityDetails: GetCommunityDetailsAndExtraDetails;
    permissions: UserPermissions;
    hasManagingPermissions: boolean;
    hasChapterLeadPermissions: boolean;
    session: Session | null;
}) => {
    const router = useRouter();
    const searchParams = useSearchParams();
    const pathname = usePathname();

    const [isProposeEventModalOpen, setIsProposeEventModalOpen] =
        useState(false);
    const isMountedRef = useRef(false);

    useEffect(() => {
        if (isMountedRef.current) return;
        isMountedRef.current = true;

        const proposeEventParam = searchParams?.get("proposeEvent");

        if (proposeEventParam === "true" && !isProposeEventModalOpen) {
            if (session?.user.id) {
                setIsProposeEventModalOpen(true);
            } else {
                //login as an anonymous user
                signIn("anonymous", { redirect: false })
                    .then((result) => {
                        if (result?.ok) {
                            setIsProposeEventModalOpen(true);
                        }
                    })
                    .catch(console.error);
            }
        }

        return () => {
            isMountedRef.current = false;
        };
    }, [searchParams]);

    // Update URL when modal state changes
    useEffect(() => {
        if (isProposeEventModalOpen) {
            const newParams = new URLSearchParams(searchParams?.toString());
            newParams.set("proposeEvent", "true");
            window.history.replaceState(
                null,
                "",
                `${pathname}?${newParams.toString()}`,
            );
        } else {
            const newParams = new URLSearchParams(searchParams?.toString());
            newParams.delete("proposeEvent");
            window.history.replaceState(
                null,
                "",
                newParams.toString()
                    ? `${pathname}?${newParams.toString()}`
                    : pathname,
            );
        }
    }, [isProposeEventModalOpen, pathname]);

    const [showFullDescription, setShowFullDescription] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [showShareCommunity, setShowShareCommunity] = useState(false);

    const { email: storeEmail } = useFollowingStore();
    const community = allCommunityDetails.communityDetails;
    const followingData = api.followers.isUserFollowing.useQuery(
        {
            communityId: community.id,
            email: storeEmail || undefined,
        },
        {
            enabled: !!session?.user.id || !!storeEmail,
        },
    );
    const isFollower = !!followingData.data;

    const coverImage = community.coverImage ? community.coverImage : undefined;
    const isHighBanner = TEMP_HIGH_BANNER_COMMUNITIES.includes(community.slug);

    const bannerStyle = coverImage?.startsWith("linear")
        ? {
              background: coverImage,
          }
        : {
              backgroundImage: `url(${`${env.NEXT_PUBLIC_ASSETS_URL}/community/${community?.id}/${community?.coverImage}`})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
          };

    const handleEditCommunity = () => {
        setEditModalOpen(!editModalOpen);
    };

    const communityDescription =
        allCommunityDetails?.communityDetails?.description;

    return (
        <>
            {/* Mobile */}
            <section className="flex w-full items-center justify-center lg:hidden">
                <div className="flex w-full flex-col gap-3 bg-v3-card-gray md:rounded-lg">
                    <SubscribeToPublishHeader status={community.status} />
                    <div
                        style={bannerStyle}
                        className={`relative z-10 h-[120px] w-full rounded-t-[10px] pb-8 pt-4`}
                    >
                        <div className="flex w-full flex-row items-center justify-end gap-2 px-2">
                            <If condition={!!hasManagingPermissions}>
                                <Button
                                    variant="tertiary"
                                    className="max-w-[150px] rounded-lg border-none bg-v3-N-500"
                                    onClick={handleEditCommunity}
                                    size="sm"
                                >
                                    <RiPencilLine className="h-5 w-5" />
                                </Button>
                            </If>
                            <button onClick={() => setShowShareCommunity(true)}>
                                <IoShareSocialOutline className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="absolute -bottom-8 h-24 w-24 rounded-full p-3 ">
                            <Link href={`/beta/${community.slug}`}>
                                <CommunityAvatar
                                    community={{
                                        id: community.id,
                                        name: community.name,
                                        image: community.image,
                                    }}
                                    className="h-20 w-20"
                                />
                            </Link>
                        </div>
                    </div>
                    <div className="flex w-full max-w-[300px] flex-row flex-wrap justify-end gap-2 self-end px-2">
                        <PartnerWithUsButton
                            communitySlug={community.slug}
                            hasSponsorshipPackages={
                                community.hasSponsorshipPackages
                            }
                        />
                        <HostAnEventButton
                            community={community}
                            permissions={permissions}
                        />
                        {/* Hardcoded for now */}
                        {(isFollower || community.slug !== "utahtechweek") && (
                            <FollowCommunityButtonBySlug
                                communitySlug={community.slug}
                                className="w-full max-w-[100px]"
                            />
                        )}
                        <If
                            condition={
                                !!permissions.isManager || !!permissions.isAdmin
                            }
                        >
                            <Link href={`/beta/${community.slug}/manage`}>
                                <Button
                                    variant="badge"
                                    size="sm"
                                    className="rounded-full"
                                >
                                    <LuExternalLink className="h-3 w-3 font-semibold" />
                                    Management
                                    <span className="-ml-1 font-thin text-v3-N300">
                                        Beta
                                    </span>
                                </Button>
                            </Link>
                        </If>
                        <DeIdConnect communitySlug={community.slug} />
                    </div>

                    <div className="flex h-full w-full items-center justify-start gap-4 px-3">
                        <div className="flex h-full w-full flex-col items-start">
                            <Link href={`/beta/${community.slug}`}>
                                <h1 className="flex gap-2 text-[22px] font-[500]">
                                    {community.name}

                                    <If
                                        condition={
                                            !!allCommunityDetails
                                                .communityDetails?.isOfficial
                                        }
                                    >
                                        <div className="flex items-center gap-2 md:justify-start">
                                            <Checkmark />
                                        </div>
                                    </If>
                                </h1>
                            </Link>

                            <div className="flex h-4 w-full gap-2 leading-none">
                                <ConditionalLink
                                    isLink={!!permissions.isManager}
                                    href={`/community-management/${community.slug}/dashboards/followers`}
                                    className="cursor-pointer hover:underline"
                                >
                                    <div className="flex gap-1 sm:hidden">
                                        <span>
                                            {allCommunityDetails.extraDetails
                                                .followers ?? 0}
                                        </span>
                                        <span>{` ${
                                            Number(
                                                allCommunityDetails.extraDetails
                                                    .followers,
                                            ) === 1
                                                ? `Follower`
                                                : `Followers`
                                        }`}</span>
                                    </div>
                                </ConditionalLink>

                                <span className="flex h-[26px] items-center justify-center leading-normal">
                                    *
                                </span>
                                <div className="flex gap-1">
                                    <span>
                                        {
                                            allCommunityDetails.extraDetails
                                                ?.upcomingEvents
                                        }
                                    </span>

                                    <span>Upcoming events</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        className="flex h-full w-full px-3"
                        onClick={() => {
                            setShowFullDescription(!showFullDescription);
                        }}
                    >
                        <p
                            className={cn(
                                "max-w-[95%] text-[16px]",
                                showFullDescription ? "" : "line-clamp-2",
                            )}
                        >
                            {communityDescription}
                        </p>

                        {/* Show the chevron to expand description if the length is over 98 characters */}
                        <If
                            condition={
                                !!communityDescription &&
                                communityDescription.length > 98
                            }
                        >
                            <div className="flex h-auto items-end justify-end">
                                {showFullDescription ? (
                                    <LuChevronUp className="h-5 w-5" />
                                ) : (
                                    <LuChevronDown className="h-5 w-5" />
                                )}
                            </div>
                        </If>
                    </div>

                    <div className="my-2 flex flex-col items-center gap-2 px-2">
                        <div className="flex w-full flex-col gap-3 md:flex-row">
                            <ul className="flex items-center gap-2">
                                {allCommunityDetails?.communityDetails?.socials.map(
                                    (link) => (
                                        <li key={link.id}>
                                            <SocialLink
                                                href={link.url}
                                                className="text-[24px]"
                                            />
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                        <div className="small-scrollbar flex h-full w-[calc(100vw-1.5rem)] items-center justify-start gap-2 overflow-x-auto pb-1 pr-2">
                            <ul className="flex gap-3 text-[14px]">
                                {allCommunityDetails?.communityDetails?.mainLinks?.map(
                                    (link, i) => (
                                        <li key={i} className="list-none">
                                            {link.name.toLocaleLowerCase() ===
                                            "host an event" ? (
                                                <HostAnEventButton
                                                    community={community}
                                                    asLink
                                                    permissions={permissions}
                                                />
                                            ) : (
                                                <ThemedLink
                                                    key={link.id}
                                                    href={link.url}
                                                    variant="secondary"
                                                    className="h-5 rounded-lg bg-white/15 p-3 px-2 text-xs text-white"
                                                    target="_blank"
                                                >
                                                    {link.name}
                                                    <RiArrowRightUpLine />
                                                </ThemedLink>
                                            )}
                                        </li>
                                    ),
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* // Desktop */}
            <div className="mind-h-screen relative hidden w-full flex-col gap-1 overflow-y-auto lg:flex">
                <SubscribeToPublishHeader status={community.status} />
                <Card className="relative w-full flex-col px-0 pt-0">
                    {coverImage && (
                        <div
                            style={bannerStyle}
                            className={cn(
                                "relative flex h-[130px] w-full justify-end gap-2 rounded-t-[10px] px-6 pt-4",
                                isHighBanner && "h-[190px]",
                            )}
                        >
                            <div className="flex max-h-11 w-full items-center justify-end gap-2">
                                <If condition={!!hasManagingPermissions}>
                                    <Button
                                        variant="tertiary"
                                        className="max-w-[150px] rounded-lg border-none bg-v3-N-500"
                                        onClick={handleEditCommunity}
                                        size="sm"
                                    >
                                        <RiPencilLine className="h-5 w-5" />
                                    </Button>
                                </If>
                                <button
                                    onClick={() => setShowShareCommunity(true)}
                                >
                                    <IoShareSocialOutline className="h-5 w-5" />
                                </button>
                            </div>
                            <div className="absolute -bottom-14 left-2 h-[130px] w-full max-w-[130px] rounded-full">
                                <Link href={`/beta/${community.slug}`}>
                                    <CommunityAvatar
                                        community={{
                                            id: community.id,
                                            name: community.name,
                                            image: community.image,
                                        }}
                                        className="h-24 w-24"
                                    />
                                </Link>
                            </div>
                        </div>
                    )}
                    <CardContent className="z-20 flex w-full flex-col items-center gap-6 pt-6 lg:flex-row lg:items-start">
                        <div className="flex h-full w-full max-w-[130px] flex-col items-center justify-start gap-4">
                            <If condition={!coverImage}>
                                <Link href={`/beta/${community.slug}`}>
                                    <CommunityAvatar
                                        community={{
                                            id: community.id,
                                            name: community.name,
                                            image: community.image,
                                        }}
                                        className="h-24 w-24"
                                    />
                                </Link>
                            </If>
                        </div>

                        <div className="flex h-full w-full flex-col justify-between gap-2">
                            <div className="flex h-full w-full flex-col gap-2">
                                <div className="flex gap-5">
                                    <Link
                                        href={`/beta/${community.slug}`}
                                        className="flex gap-2"
                                    >
                                        <h1 className="w-max text-[24px] font-[500]">
                                            {community.name}
                                        </h1>
                                        <If
                                            condition={
                                                !!allCommunityDetails
                                                    .communityDetails
                                                    ?.isOfficial
                                            }
                                        >
                                            <div className="hidden items-center gap-2 md:flex 2xl:hidden">
                                                <Checkmark />
                                            </div>
                                        </If>
                                    </Link>
                                    <div className="flex w-full justify-start gap-2">
                                        <div />

                                        <div className="hidden items-center justify-center gap-2 md:flex">
                                            <PartnerWithUsButton
                                                communitySlug={community.slug}
                                                hasSponsorshipPackages={
                                                    community.hasSponsorshipPackages
                                                }
                                            />
                                            <HostAnEventButton
                                                community={community}
                                                permissions={permissions}
                                            />
                                            {(isFollower ||
                                                community.slug !==
                                                    "utahtechweek") && (
                                                <FollowCommunityButtonBySlug
                                                    communitySlug={
                                                        community.slug
                                                    }
                                                    className="w-[140px]"
                                                />
                                            )}
                                            <If
                                                condition={
                                                    !!permissions.isManager ||
                                                    !!permissions.isAdmin
                                                }
                                            >
                                                <Link
                                                    href={`/beta/${community.slug}/manage`}
                                                >
                                                    <Button
                                                        variant="badge"
                                                        size="sm"
                                                        className="rounded-full"
                                                    >
                                                        <LuExternalLink className="h-3 w-3 font-semibold" />
                                                        Management
                                                        <span className="-ml-1 font-thin text-v3-N300">
                                                            Beta
                                                        </span>
                                                    </Button>
                                                </Link>
                                            </If>

                                            <DeIdConnect
                                                communitySlug={community.slug}
                                            />
                                            <If condition={!coverImage}>
                                                <If
                                                    condition={
                                                        !!hasManagingPermissions
                                                    }
                                                >
                                                    <Button
                                                        variant="tertiary"
                                                        className="max-w-[150px] rounded-lg border-none bg-v3-N-500"
                                                        onClick={
                                                            handleEditCommunity
                                                        }
                                                        size="sm"
                                                    >
                                                        <RiPencilLine className="h-5 w-5" />
                                                    </Button>
                                                </If>
                                            </If>
                                            <button
                                                onClick={() =>
                                                    setShowShareCommunity(true)
                                                }
                                                className="ml-auto"
                                            >
                                                <IoShareSocialOutline className="h-5 w-5" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <p className="text-[16px]">
                                    {
                                        allCommunityDetails?.communityDetails
                                            ?.description
                                    }
                                </p>

                                <div className="flex items-center gap-2">
                                    <ul className="flex gap-2">
                                        {allCommunityDetails?.communityDetails?.socials.map(
                                            (link) => (
                                                <li key={link.id}>
                                                    <SocialLink
                                                        href={link.url}
                                                        className="text-[16px]"
                                                    />
                                                </li>
                                            ),
                                        )}
                                    </ul>

                                    <span>•</span>
                                    <ul className="flex flex-wrap gap-3 text-[14px]">
                                        {allCommunityDetails?.communityDetails?.mainLinks?.map(
                                            (link, i) => (
                                                <li
                                                    key={i}
                                                    className="list-none"
                                                >
                                                    {link.name.toLocaleLowerCase() ===
                                                    "host an event" ? (
                                                        <HostAnEventButton
                                                            community={
                                                                community
                                                            }
                                                            permissions={
                                                                permissions
                                                            }
                                                            asLink
                                                        />
                                                    ) : (
                                                        <ThemedLink
                                                            key={link.id}
                                                            href={link.url}
                                                            variant="secondary"
                                                            className="underline"
                                                            target="_blank"
                                                        >
                                                            {link.name}
                                                        </ThemedLink>
                                                    )}
                                                </li>
                                            ),
                                        )}
                                    </ul>
                                </div>

                                <div className="flex flex-col items-center gap-1 text-sm text-v3-N-200 xs:flex-row xs:items-start xs:gap-5">
                                    <ConditionalLink
                                        isLink={!!permissions.isManager}
                                        href={`/community-management/${community.slug}/dashboards/followers`}
                                        className="cursor-pointer hover:underline"
                                    >
                                        <div className="hidden gap-1 sm:flex">
                                            <span>
                                                {allCommunityDetails
                                                    .extraDetails?.followers ??
                                                    0}
                                            </span>
                                            <span>{` ${
                                                allCommunityDetails.extraDetails
                                                    .followers === "1"
                                                    ? `Follower`
                                                    : `Followers`
                                            }`}</span>
                                        </div>
                                    </ConditionalLink>
                                    <span className="flex h-[26px] items-center justify-center leading-normal">
                                        *
                                    </span>
                                    <div className="flex gap-1">
                                        <span>
                                            {
                                                allCommunityDetails.extraDetails
                                                    ?.upcomingEvents
                                            }
                                        </span>
                                        <span>Upcoming events</span>
                                    </div>
                                    <span className="flex h-[26px] items-center justify-center leading-normal">
                                        *
                                    </span>
                                    <div className="flex gap-1">
                                        <span>
                                            {
                                                allCommunityDetails
                                                    .communityDetails
                                                    ?.totalEventsOnRiver
                                            }
                                        </span>
                                        <span>Total events on River</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                <EditCommunitySheet
                    isOpen={editModalOpen}
                    setIsOpen={setEditModalOpen}
                    community={allCommunityDetails["communityDetails"]}
                    onSuccess={() => {
                        toast.success("Community updated successfully", {
                            autoClose: 1000,
                        });
                        router.refresh();
                    }}
                />
                <ShareCommunityModal
                    isVisible={showShareCommunity}
                    setIsVisible={setShowShareCommunity}
                    community={community}
                />
                <EventProposeModal
                    community={community}
                    setOpen={setIsProposeEventModalOpen}
                    open={isProposeEventModalOpen}
                    hasManagingPermissions={
                        permissions.isManager || permissions.isAdmin
                    }
                />
            </div>
        </>
    );
};
